import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { config, supabaseConfigured } from '../shared/config';

/**
 * Browser Supabase client — the same project and anon key the Expo app uses,
 * so a session created on either surface is the same identity to the backend.
 * `detectSessionInUrl` is on here (unlike Expo) because the magic link lands
 * back in this tab with the tokens in the URL fragment.
 */
let client: SupabaseClient | null = null;

export function supabase(): SupabaseClient {
  if (!client) {
    if (!supabaseConfigured) throw new Error('Supabase is not configured (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY)');
    client = createClient(config.supabaseUrl, config.supabaseAnonKey, {
      auth: { autoRefreshToken: true, persistSession: true, detectSessionInUrl: true, storage: window.localStorage },
    });
  }
  return client;
}

/** Test seam: swap in a fake client without touching module internals elsewhere. */
export function setSupabaseClientForTests(fake: SupabaseClient | null): void {
  client = fake;
}

export const authRedirectUrl = () => `${window.location.origin}/sign-in${window.location.search}`;

export async function sendMagicLink(email: string): Promise<void> {
  const { error } = await supabase().auth.signInWithOtp({
    email,
    options: { emailRedirectTo: authRedirectUrl(), shouldCreateUser: true },
  });
  if (error) throw error;
}

/**
 * Second half of email sign-in: the same email carries a link AND a one-time
 * code. Typing the code signs in this tab directly, so it works when the link
 * would open in another browser, get rewritten by link tracking, or be spent
 * by a mail scanner before the person clicks it.
 */
export async function verifyEmailCode(email: string, code: string): Promise<Session> {
  const { data, error } = await supabase().auth.verifyOtp({ email, token: code, type: 'email' });
  if (error) throw error;
  if (!data.session) throw new Error('That code did not sign you in. Request a new one.');
  return data.session;
}

export async function signInWithPassword(email: string, password: string): Promise<Session> {
  const { data, error } = await supabase().auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.session!;
}

export async function currentAccessToken(): Promise<string | null> {
  if (config.devToken) return config.devToken;
  if (!supabaseConfigured) return null;
  const { data } = await supabase().auth.getSession();
  return data.session?.access_token ?? null;
}

export async function signOut(): Promise<void> {
  if (supabaseConfigured) await supabase().auth.signOut();
}
